import type { Material } from '@amarantos/api-client'
import {
	getListProjectGroupedMaterialsQueryOptions,
	useListProjectGroupedMaterialsSuspense,
} from '@amarantos/api-client'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Plus, RefreshCw } from 'lucide-react'
import { useCallback, useDeferredValue } from 'react'
import { useTranslation } from 'react-i18next'
import { pick } from 'remeda'
import { toast } from 'sonner'
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog'
import { DeleteButton } from '@/components/delete-button'
import { ListSearchInput } from '@/components/list-search-input'
import { ListToolbar } from '@/components/list-toolbar'
import { PaginationBar } from '@/components/pagination-bar'
import { RouteErrorFallback } from '@/components/route-error-fallback'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { materialsSearchSchema, toListProjectMaterialsParams } from '@/features/materials'
import { MaterialFormDrawer, type MaterialFormDrawerState } from '@/features/materials/components/material-form-drawer'
import { MaterialsFilters } from '@/features/materials/components/materials-filters'
import { MaterialsTable } from '@/features/materials/components/materials-table'
import {
	useDeleteMaterialMutation,
	useRecalculateMaterialsMutation,
} from '@/features/materials/hooks/use-material-mutations'
import { isManualMaterial } from '@/features/materials/utils/material-labels'
import { useConfirmAction } from '@/hooks/use-confirm-action'
import { getApiErrorMessage } from '@/utils/errors'

// System rows are written by network recalculation and read-only here, so edit and delete are
// offered on manual rows alone.
export const Route = createFileRoute('/_authenticated/projects/$projectId/supplies')({
	validateSearch: materialsSearchSchema,
	loaderDeps: ({ search }) => search,
	loader: ({ context: { queryClient }, params: { projectId }, deps }) => {
		queryClient.ensureQueryData(
			getListProjectGroupedMaterialsQueryOptions(projectId, toListProjectMaterialsParams(deps)),
		)
	},
	errorComponent: ({ error, reset }) => (
		<RouteErrorFallback error={error} reset={reset} fallbackMessageKey='error.load_materials' />
	),
	component: ProjectSuppliesRoute,
})

function ProjectSuppliesRoute() {
	const { t } = useTranslation()
	const { projectId } = Route.useParams()
	const search = Route.useSearch()
	const navigate = Route.useNavigate()
	const deferredSearch = useDeferredValue(search)
	const isStale = deferredSearch !== search
	const isFiltered = !!deferredSearch.search || deferredSearch.provenance !== 'all'

	const groupsQuery = useListProjectGroupedMaterialsSuspense(projectId, toListProjectMaterialsParams(deferredSearch))

	const deleteMaterial = useConfirmAction<Material, { projectId: number; id: Material['id'] }>({
		mutation: useDeleteMaterialMutation(projectId),
		toVariables: (material) => ({ projectId, id: material.id }),
		formatError: (err) => getApiErrorMessage(err, t('error.delete_material')),
	})

	const recalculate = useRecalculateMaterialsMutation(projectId)

	function recalculateMaterials() {
		recalculate.mutate(
			{ projectId },
			{
				onSuccess: (result) =>
					toast.success(
						t('materials.recalculate.success', { count: result.requirements_count, issues: result.issues.length }),
					),
				onError: (err) => toast.error(getApiErrorMessage(err, t('error.recalculate_materials'))),
			},
		)
	}

	const groups = groupsQuery.data.data
	const totalPages = groupsQuery.data.meta.total_pages
	const drawerState: MaterialFormDrawerState = pick(search, ['dialog', 'materialId'])
	const drawerOpen = drawerState.dialog !== undefined

	const onSearchChange = useCallback(
		(nextValue: string) => {
			navigate({ replace: true, search: (previous) => ({ ...previous, search: nextValue, page: 1 }) })
		},
		[navigate],
	)

	return (
		<>
			<Card>
				<CardContent className='space-y-4'>
					<ListToolbar
						actions={
							<>
								<Button type='button' variant='outline' disabled={recalculate.isPending} onClick={recalculateMaterials}>
									<RefreshCw className={recalculate.isPending ? 'animate-spin' : undefined} />
									{t('materials.recalculate.button')}
								</Button>
								<Button asChild>
									<Link
										to='.'
										search={(previous) => ({ ...previous, dialog: 'create' as const, materialId: undefined })}
									>
										<Plus />
										{t('materials.create.button')}
									</Link>
								</Button>
							</>
						}
					>
						<ListSearchInput
							value={search.search}
							onChange={onSearchChange}
							placeholder={t('materials.search.placeholder')}
						/>
						<MaterialsFilters onApply={() => navigate({ search: (previous) => ({ ...previous, page: 1 }) })} />
					</ListToolbar>

					<MaterialsTable
						groups={groups}
						isStale={isStale}
						isFiltered={isFiltered}
						actions={(material) =>
							isManualMaterial(material) && (
								<>
									<Button asChild variant='outline' size='sm'>
										<Link
											to='.'
											search={(previous) => ({ ...previous, dialog: 'edit' as const, materialId: material.id })}
										>
											{t('materials.edit.button')}
										</Link>
									</Button>
									<DeleteButton onClick={() => deleteMaterial.request(material)} />
								</>
							)
						}
					/>

					<PaginationBar
						currentPage={search.page}
						totalPages={totalPages}
						onPageChange={(page) => navigate({ search: (previous) => ({ ...previous, page }) })}
					/>
				</CardContent>
			</Card>

			<MaterialFormDrawer
				{...drawerState}
				projectId={projectId}
				open={drawerOpen}
				onOpenChange={(isOpen) => {
					if (!isOpen) {
						navigate({ search: (previous) => ({ ...previous, dialog: undefined, materialId: undefined }) })
					}
				}}
			/>

			<ConfirmDeleteDialog
				{...deleteMaterial.dialogProps}
				title={t('materials.delete.title')}
				description={t('materials.delete.description', {
					name: deleteMaterial.target?.display_name ?? t('materials.delete.fallback_name'),
				})}
			/>
		</>
	)
}
